import PageHeader from "@/components/PageHeader";
import SectionHead from "@/components/SectionHead";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import { TechLabel } from "@/components/Bits";
import usePageMeta from "@/hooks/usePageMeta";
import { COMPANY, STORIA, VALUES, TEAM, CERTS, IMG } from "@/config/company";
import { MapPin, Award, CalendarDays } from "lucide-react";

export default function ChiSiamo() {
  usePageMeta(
    "Chi siamo — MMC S.r.l. | Carpenteria Metallica dal 2010",
    "Fondata nel 2010, MMC S.r.l. realizza parapetti, cancelli, corrimani, scale interne ed esterne e strutture metalliche su misura in provincia di Bergamo."
  );

  return (
    <main>
      <PageHeader
        index="01"
        eyebrow="Azienda"
        title="Chi siamo"
        sub="Metallo, officina e metodo: dal 2010 trasformiamo il metallo in soluzioni solide, funzionali e curate in ogni dettaglio."
      />

      {/* STORIA */}
      <section data-testid="chi-siamo-storia" className="px-5 py-20 md:px-10 md:py-28 lg:px-16">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="clip-notch relative h-[420px] overflow-hidden border border-white/10 md:h-[520px]">
                <img
                  src={IMG.realCapriate}
                  alt="Capannone industriale con capriate in acciaio realizzato da MMC"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <div className="spotlight absolute inset-0" />
                <TechLabel className="absolute bottom-4 left-4">Fig. 02 — Capriate in acciaio MMC</TechLabel>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <span className="inline-flex items-center gap-2 border border-white/15 px-3.5 py-2 font-tech text-[10px] uppercase tracking-[0.2em] text-white/60">
                  <CalendarDays className="size-3.5 text-molten" /> Dal {COMPANY.founded}
                </span>
                <span className="border border-white/15 px-3.5 py-2 font-tech text-[10px] uppercase tracking-[0.2em] text-white/60">
                  {COMPANY.sector}
                </span>
                <span className="border border-white/15 px-3.5 py-2 font-tech text-[10px] uppercase tracking-[0.2em] text-white/60">
                  Progetti su misura
                </span>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-6">
            <Reveal>
              <TechLabel className="text-molten">La nostra storia</TechLabel>
              <h2 className="mt-5 font-display text-4xl font-semibold uppercase leading-tight tracking-tight sm:text-5xl">
                La cultura del metallo, ogni giorno
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-6 leading-relaxed md:text-lg">
                <p className="border-l-2 border-molten pl-5 text-white/90">{STORIA[0]}</p>
                {STORIA.slice(1).map((p, i) => (
                  <p key={i} className="text-fog">
                    {p}
                  </p>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALORI */}
      <section data-testid="chi-siamo-valori" className="border-y border-white/10 bg-steel px-5 py-20 md:px-10 md:py-28 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionHead index="02" eyebrow="I nostri valori" title="Quattro parole, un metodo" />
          <div className="mt-0 border-t border-white/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.05}>
                <div className="group grid grid-cols-1 gap-3 border-b border-white/10 py-8 transition-colors duration-300 hover:bg-white/[0.02] md:grid-cols-12 md:items-baseline md:gap-6 md:py-10">
                  <span className="font-tech text-sm text-molten md:col-span-2">/{v.n}</span>
                  <h3 className="font-display text-3xl font-semibold uppercase tracking-tight transition-transform duration-300 group-hover:translate-x-2 md:col-span-4">
                    {v.title}
                  </h3>
                  <p className="max-w-xl leading-relaxed text-fog md:col-span-6">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section data-testid="chi-siamo-team" className="px-5 py-20 md:px-10 md:py-28 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionHead
            index="03"
            eyebrow="Il team"
            title="Persone dietro le strutture"
            sub="Ruoli chiari e responsabilità precise: è così che una commessa diventa una consegna, senza sorprese."
          />
          <div className="mt-12 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {TEAM.map((t, i) => (
              <Reveal key={t.role} delay={(i % 3) * 0.08} className="h-full bg-ink">
                <div className="flex h-full flex-col p-7">
                  <span className="font-tech text-xs text-molten">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-xl font-medium uppercase tracking-tight">{t.role}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-fog">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SEDE */}
      <section data-testid="chi-siamo-sede" className="border-y border-white/10 bg-steel px-5 py-20 md:px-10 md:py-28 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionHead index="04" eyebrow="Sede e officina" title="Vieni a trovarci in officina" />
          <div className="mt-12 grid gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="flex h-full flex-col justify-between gap-10 border border-white/10 p-8">
                <div className="flex items-start gap-4">
                  <MapPin className="mt-1 size-5 shrink-0 text-molten" />
                  <div>
                    <TechLabel>Indirizzo</TechLabel>
                    <p className="mt-2 text-lg text-white/85">{COMPANY.addressDisplay}</p>
                    <a
                      href="/contatti"
                      data-testid="chi-siamo-mappa-link"
                      className="mt-4 inline-block font-tech text-[10px] uppercase tracking-[0.25em] text-molten underline-offset-4 hover:underline"
                    >
                      Vedi mappa e contatti
                    </a>
                  </div>
                </div>
                <div>
                  <TechLabel>Orari di apertura</TechLabel>
                  <ul className="mt-4 space-y-2.5">
                    {COMPANY.hours.map((h) => (
                      <li
                        key={h.d}
                        data-testid={`chi-siamo-orario-${h.d.toLowerCase().split(" ")[0]}`}
                        className="flex items-baseline justify-between gap-6 border-b border-white/5 pb-2.5 text-sm"
                      >
                        <span className="text-white/75">{h.d}</span>
                        <span className="font-tech text-xs tracking-wider text-white/60">{h.h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex h-full flex-col gap-6">
                <TechLabel className="text-molten">Certificazioni</TechLabel>
                {CERTS.map((c, i) => (
                  <div
                    key={c.code}
                    data-testid={`chi-siamo-cert-${i}`}
                    className="flex items-center gap-5 border border-white/10 p-7 transition-colors duration-300 hover:border-molten/60"
                  >
                    <Award className="size-8 shrink-0 text-molten" strokeWidth={1.5} />
                    <div>
                      <p className="font-display text-2xl font-semibold uppercase tracking-tight">{c.code}</p>
                      <p className="mt-1 text-sm text-fog">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
